/*
 * @prop --component-table-head-color: text color of table head
 * @prop --component-table-first-column-color: text color of first column
*/

@mixin headerColor($color) {
  thead {
    background-color: $color;

    th,
    th:first-of-type {
      background-color: $color;
    }
  }
}

@mixin columnColor($color, $column) {
  tr td:nth-child(#{$column}) {
    background-color: $color;
  }
}

.ui-table {
  display: inline-block;
  font-size: 15px;
  width: 100%;

  .table-wrapper {
    overflow-x: auto;
  }

  table {
    border: none;
    border-collapse: separate;
    border-spacing: inherit;
    color: var(--skin-color-text, $ui-dark);
  }

  tr th,
  tr td {
    font-weight: normal;
    line-height: 20px;
    text-align: left;
    white-space: nowrap;
    border-right: 1px solid var(--skin-color-secondary, $ui-grey);
    border-left: none;
    border-top: none;
    vertical-align: top;

    &:last-of-type {
      border-right: none;
    }
  }

  thead {
    color: var(--component-table-head-color, var(--skin-color-text, $ui-dark));

    tr th {
      border-top: 1px solid var(--skin-color-secondary, $ui-grey);
      padding: 17px 8px;
      text-overflow: clip;

      @include breakpoint($xlarge) {
        padding-left: 15px;
        padding-right: 15px;
      }
    }
  }

  p.dataTable__barChart {
    background-color: $ui-grey-minus-1;
    width: var(--chart-item-width, 100%);
    height: 20px;
  }

  tbody {
    tr {
      border-spacing: 0;

      &:hover {
        cursor: default;
      }

      &:first-of-type td {
        border-top: 1px solid var(--skin-color-secondary, $ui-grey);
      }

      td {
        align-items: normal;
        padding: 14px 8px;
        border-bottom: 1px solid var(--skin-color-secondary, $ui-grey);

        @include breakpoint($xlarge) {
          padding-left: 15px;
          padding-right: 15px;
        }
      }
    }
  }

  // first column sticky
  &.first-column-frozen tr {
    position: relative;

    th:first-of-type,
    td:first-of-type {
      background-color: var(--skin-color-white, $ui-white);
      left: 0;
      position: sticky;
      z-index: 1;
    }

    td:first-of-type {
      color: var(
        --component-table-first-column-color,
        var(--skin-color-text, $ui-dark)
      );
    }
  }

  &.first-column-bold,
  &.first-column-as-header {
    tr th,
    tr td {
      &:first-child {
        font-weight: $font-weight-bold;

        * {
          font-weight: $font-weight-bold;
        }
      }
    }
  }

  &.first-row-bold,
  &.first-row-as-header {
    thead {
      tr th {
        font-weight: $font-weight-bold;

        * {
          font-weight: $font-weight-bold;
        }
      }
    }

    &.no-header tbody tr:first-of-type td {
      font-weight: $font-weight-bold;
    }
  }

  // no vertical borders
  &.light {
    tr th,
    tr td {
      border-right: none;
    }
  }

  // backgrounds options
  // header in color gray
  &.headline-gray {
    &.first-row-as-header {
      @include headerColor(
        var(--skin-color-secondary-lighter, $ui-grey-minus-3)
      );

      &.no-header tbody tr:first-of-type td {
        background-color: var(--skin-color-secondary-lighter, $ui-grey-minus-3);
      }
    }

    &.first-column-as-header {
      @include columnColor(
        var(--skin-color-secondary-lighter, $ui-grey-minus-3),
        1
      );

      thead tr:first-of-type th:first-of-type {
        background-color: var(--skin-color-secondary-lighter, $ui-grey-minus-3);
      }
    }
  }

  // header in color blue
  &.headline-blue {
    &.first-row-as-header {
      @include headerColor($ui-blue-minus-3);

      &.no-header tbody tr:first-of-type td {
        background-color: $ui-blue-minus-3;
      }
    }

    &.first-column-as-header {
      @include columnColor($ui-blue-minus-3, 1);

      thead tr:first-of-type th:first-of-type {
        background-color: $ui-blue-minus-3;
      }
    }
  }

  // odd rows with background gray
  &.background--alternating-gray {
    &.no-header tbody tr:nth-of-type(even) td,
    &:not(.no-header) tbody tr:nth-of-type(odd) td {
      background-color: $ui-grey-minus-1;

      &:hover {
        background-color: $ui-grey-minus-1;
      }
    }
  }

  // odd rows with background blue
  &.background--alternating-blue {
    &.no-header tbody tr:nth-of-type(even) td,
    &:not(.no-header) tbody tr:nth-of-type(odd) td {
      background-color: $ui-blue-minus-3;

      &:hover {
        background-color: $ui-blue-minus-3;
      }
    }
  }

  // first column gray
  &.first-column-gray {
    @include columnColor(
      var(--skin-color-secondary-lighter, $ui-grey-minus-3),
      1
    );

    thead tr:first-of-type th:first-of-type {
      background-color: var(--skin-color-secondary-lighter, $ui-grey-minus-3);
    }
  }

  .fullWidth {
    width: 100%;
  }

  .fixed {
    table-layout: fixed;
  }

  .auto {
    table-layout: auto;

    tr th,
    tr td {
      white-space: normal;
    }
  }

  .minWidth {
    min-width: 1268px;
  }
}
